import React, { useEffect, Suspense } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSelector } from "react-redux";
import Home from "./screens/Home";
import Profile from "./screens/Profile";
import ResponsiveAppBar from "./components/ResponsiveAppBar";
import LoadingSpinner from "./components/LoadingSpinner";
import lazyWithRetry from "./components/lazyWithRetry";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import { selectUser, initializeUser } from "./features/user/userSlice";
import { IS_LOCAL } from "./features/comandas/services/localMode";
import { store } from "./app/store";
import { I18nProvider } from "./v2/i18n/I18nProvider";
import { initForegroundMessages } from "./features/reservations/services/fcm";
// Bandera del piloto de "Costos y stock" + la redirección de /comandas/stock: módulos chicos,
// van en el bundle principal porque deciden QUÉ rutas se declaran (no se pueden lazy-loadear).
import { isCostsV3 } from "./features/costs/v3/flag";
import ComandasStockRedirect from "./features/costs/v3/ComandasStockRedirect";

// Lazy-loaded routes — cada feature pesada se carga solo al entrar.
// Esto evita tirar Konva/react-pdf/leaflet/editor en el bundle inicial.
const MenuEditor = lazyWithRetry(() => import("./screens/MenuEditor"));
const MenuEditorV2 = lazyWithRetry(() => import("./v2/screens/MenuEditorV2"));
const MenuDashboard = lazyWithRetry(() =>
  import("./features/menu-dashboard").then((m) => ({ default: m.MenuDashboard }))
);
const PrintPdfScreen = lazyWithRetry(() =>
  import("./features/print-pdf").then((m) => ({ default: m.PrintPdfScreen }))
);
// CostsEntry elige entre Costos y stock (v3, piloto) y el CostsScreen de siempre.
const CostsEntry = lazyWithRetry(() =>
  import("./features/costs").then((m) => ({ default: m.CostsEntry }))
);
const ReservationsLayout = lazyWithRetry(() =>
  import("./features/reservations").then((m) => ({ default: m.ReservationsLayout }))
);
const TodayServiceScreen = lazyWithRetry(() =>
  import("./features/reservations").then((m) => ({ default: m.TodayServiceScreen }))
);
const ReservationsScreen = lazyWithRetry(() =>
  import("./features/reservations").then((m) => ({ default: m.ReservationsScreen }))
);
const FloorPlanEditorScreen = lazyWithRetry(() =>
  import("./features/reservations").then((m) => ({ default: m.FloorPlanEditorScreen }))
);
const ReservationsConfigScreen = lazyWithRetry(() =>
  import("./features/reservations").then((m) => ({ default: m.ReservationsConfigScreen }))
);
const PublicBookingScreen = lazyWithRetry(() =>
  import("./features/reservations").then((m) => ({ default: m.PublicBookingScreen }))
);
const ManageBookingScreen = lazyWithRetry(() =>
  import("./features/reservations").then((m) => ({ default: m.ManageBookingScreen }))
);
const StatsScreen = lazyWithRetry(() =>
  import("./features/reservations").then((m) => ({ default: m.StatsScreen }))
);
const ClientsScreen = lazyWithRetry(() =>
  import("./features/reservations").then((m) => ({ default: m.ClientsScreen }))
);
const CashRegisterLayout = lazyWithRetry(() =>
  import("./features/cash-register").then((m) => ({ default: m.CashRegisterLayout }))
);
const CashRegisterTodayScreen = lazyWithRetry(() =>
  import("./features/cash-register").then((m) => ({ default: m.CashRegisterTodayScreen }))
);
const CashRegisterHistoryScreen = lazyWithRetry(() =>
  import("./features/cash-register").then((m) => ({ default: m.CashRegisterHistoryScreen }))
);
const CashRegisterConfigScreen = lazyWithRetry(() =>
  import("./features/cash-register").then((m) => ({ default: m.CashRegisterConfigScreen }))
);
const ComandasLayout = lazyWithRetry(() =>
  import("./features/comandas").then((m) => ({ default: m.ComandasLayout }))
);
const ComandasTurnoScreen = lazyWithRetry(() =>
  import("./features/comandas").then((m) => ({ default: m.ComandasTurnoScreen }))
);
const ComandasMesaScreen = lazyWithRetry(() =>
  import("./features/comandas").then((m) => ({ default: m.ComandasMesaScreen }))
);
const ComandasConfigScreen = lazyWithRetry(() =>
  import("./features/comandas").then((m) => ({ default: m.ComandasConfigScreen }))
);
const ComandasHistorialScreen = lazyWithRetry(() =>
  import("./features/comandas").then((m) => ({ default: m.ComandasHistorialScreen }))
);
const ComandasStockScreen = lazyWithRetry(() =>
  import("./features/comandas").then((m) => ({ default: m.ComandasStockScreen }))
);
// El editor del plano del salón va en su PROPIO pedazo (no por el barrel): la POS del local no lo baja.
const ComandasPlanoScreen = lazyWithRetry(() => import("./features/comandas/screens/ComandasPlanoScreen"));
const AdminLayout = lazyWithRetry(() => import("./admin/AdminLayout"));
const AdminDashboard = lazyWithRetry(() => import("./admin/AdminDashboard"));
const CreateClient = lazyWithRetry(() => import("./admin/CreateClient"));
const ClientDetail = lazyWithRetry(() => import("./admin/ClientDetail"));
const RedirectManager = lazyWithRetry(() => import("./admin/RedirectManager"));
const AdminMenuEditor = lazyWithRetry(() => import("./admin/AdminMenuEditor"));
const CobrosScreen = lazyWithRetry(() => import("./admin/CobrosScreen"));
const TasksScreen = lazyWithRetry(() => import("./admin/TasksScreen"));
const PlataScreen = lazyWithRetry(() => import("./admin/PlataScreen"));


function App() {
  const user = useSelector(selectUser);

  // Llama a initializeUser() cuando el componente se monta
  useEffect(() => {
    store.dispatch(initializeUser());
  }, []); // [] asegura que se ejecute solo una vez al montar el componente

  return (
    // MODO LOCAL (POS servida por el agente, sin login): no hay usuario del que tomar el idioma,
    // así que por defecto detectaba el del NAVEGADOR de la PC del local (una Win en inglés → POS en
    // inglés). Forzamos español, que es el idioma de los clientes. En la nube sigue autodetectando.
    <I18nProvider initialLocale={IS_LOCAL ? "es" : null}>
      <div className="App">
        <BrowserRouter>
          <SwNavigationBridge />
          <ConditionalAppBar />
          <LocationAwareBoundary>
            <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Ruta pública: accesible siempre, sin login */}
              {/* Provider propio para el widget: arranca en el idioma del local
                  (lo setea PublicBookingScreen al cargar config) y el cambio del
                  cliente no toca el idioma del admin. */}
              <Route
                path="/book/:slug"
                element={
                  <I18nProvider persist={false}>
                    <PublicBookingScreen />
                  </I18nProvider>
                }
              />

              {/* Auto-gestión de una reserva por el cliente (link con token). */}
              <Route
                path="/r/:uid/:menuId/:bookingId"
                element={
                  <I18nProvider persist={false}>
                    <ManageBookingScreen />
                  </I18nProvider>
                }
              />

              {user ? (
                <>
                  <Route path="/" element={<Navigate to={IS_LOCAL && window.__COMANDAS_LOCAL__ ? `/profile/${user.uid}/${window.__COMANDAS_LOCAL__.menuId}/comandas` : `/profile/${user.uid}`} replace />} />
                  <Route path="/home" element={<Home />} />
                  <Route element={<PrivateRouteWrapper />}>
                    <Route
                      path="/profile/:uid/:menuId/panel"
                      element={<MenuDashboard />}
                    />
                    <Route
                      path="/profile/:uid/:menuId/pdf"
                      element={<PrintPdfScreen />}
                    />
                    {/* Costos: `costs/*` porque la UI nueva (v3) tiene rutas propias
                        (plato/:id, insumos, ajustes…) con <Routes> descendientes. La vieja
                        no: CostsEntry la manda a la base si llega con un sub-path. */}
                    <Route
                      path="/profile/:uid/:menuId/costs/*"
                      element={<CostsEntry />}
                    />
                    {/* Un solo ReservationsProvider (ReservationsLayout) para
                        todo el módulo: las suscripciones live persisten entre
                        Hoy/Mes/Mapa/Config/Día/Stats en vez de recrearse. */}
                    <Route
                      path="/profile/:uid/:menuId/reservas"
                      element={<ReservationsLayout />}
                    >
                      <Route index element={<TodayServiceScreen />} />
                      <Route path="mes" element={<ReservationsScreen />} />
                      <Route path="mapa" element={<FloorPlanEditorScreen />} />
                      <Route path="config" element={<ReservationsConfigScreen />} />
                      <Route path="dia/:date" element={<TodayServiceScreen />} />
                      <Route path="stats" element={<StatsScreen />} />
                      <Route path="clientes" element={<ClientsScreen />} />
                    </Route>
                    {/* Caja ("Hacer la caja"): un solo CashRegisterProvider
                        (CashRegisterLayout) para todo el módulo, igual que
                        reservas. Owner-only y sin lectura pública. */}
                    <Route
                      path="/profile/:uid/:menuId/caja"
                      element={<CashRegisterLayout />}
                    >
                      <Route index element={<CashRegisterTodayScreen />} />
                      <Route path="dia/:date" element={<CashRegisterTodayScreen />} />
                      <Route path="historial" element={<CashRegisterHistoryScreen />} />
                      <Route path="config" element={<CashRegisterConfigScreen />} />
                    </Route>
                    {/* Con Costos y stock (v3) el stock vive en Costos › Insumos.
                        /comandas/stock (favoritos viejos) redirige SIN montar
                        ComandasProvider: por eso es HERMANA del layout y no hija.
                        OJO, el orden importa: las dos rutas puntúan igual y no son
                        "hermanas" para React Router (distinta profundidad), así que
                        gana la declarada PRIMERO. Tiene que quedar ARRIBA del layout
                        (lo cuida src/features/costs/v3/comandasStockRoute.test.jsx). */}
                    {isCostsV3(user.uid) && (
                      <Route
                        path="/profile/:uid/:menuId/comandas/stock"
                        element={<ComandasStockRedirect />}
                      />
                    )}
                    {/* Comandas: un solo ComandasProvider (ComandasLayout) para
                        todo el módulo. Owner-only. Alimenta la caja (getDayTotals
                        FromComandas) y escribe la cola /print_jobs que lee el
                        agente local. */}
                    <Route
                      path="/profile/:uid/:menuId/comandas"
                      element={<ComandasLayout />}
                    >
                      <Route index element={<ComandasTurnoScreen />} />
                      <Route path="mesa/:tableId" element={<ComandasMesaScreen />} />
                      <Route path="historial" element={<ComandasHistorialScreen />} />
                      <Route path="config" element={<ComandasConfigScreen />} />
                      <Route path="config/plano" element={<ComandasPlanoScreen />} />
                      <Route path="stock" element={<ComandasStockScreen />} />
                    </Route>
                    <Route
                      path="/profile/:uid/:menuId"
                      element={<MenuEditor />}
                    />
                    <Route path="/profile/:uid" element={<Profile />} />

                    {/* Rutas V2 - Nuevas */}
                    <Route path="/v2/editor/:menuId" element={<MenuEditorV2 />} />

                    {/* Rutas Admin */}
                    <Route path="/admin" element={<AdminLayout />}>
                      <Route index element={<AdminDashboard />} />
                      <Route path="new" element={<CreateClient />} />
                      <Route path="client/:uid" element={<ClientDetail />} />
                      <Route path="cobros" element={<CobrosScreen />} />
                      <Route path="plata" element={<PlataScreen />} />
                      <Route path="tareas" element={<TasksScreen />} />
                      <Route path="redirects" element={<RedirectManager />} />
                    </Route>
                    {/* Editor de menú admin (fullscreen, fuera del AdminLayout) */}
                    <Route path="/admin/edit/:uid/:version/:slug" element={<AdminMenuEditor />} />
                  </Route>
                </>
              ) : (
                <Route path="/" element={<Home />} />
              )}
              {!user && <Route path="*" element={<HomeRememberingAdmin />} />}
            </Routes>
            </Suspense>
          </LocationAwareBoundary>
        </BrowserRouter>
      </div>
    </I18nProvider>
  );
}

/* Sin sesión, cualquier ruta va a la landing. Si era el admin (el ícono "RA Admin"
 * instalado abre /admin y en iPhone arranca sin sesión), la recordamos para volver ahí
 * después del login (ResponsiveAppBar). */
function HomeRememberingAdmin() {
  const location = useLocation();
  if (/^\/admin(\/|$)/.test(location.pathname)) {
    try { sessionStorage.setItem("ra_after_login", location.pathname + location.search); } catch { /* sin storage */ }
  }
  return <Navigate to="/" replace />;
}

function PrivateRouteWrapper() {
  return <Outlet />;
}

/* Puente que le pasa la ruta actual al ChunkErrorBoundary. Vive DENTRO del
 * BrowserRouter (App está por encima y no puede usar useLocation). Sirve para
 * que, si el boundary quedó mostrando el error, se limpie al cambiar de ruta.
 * Pasamos la ruta como prop (NO como key) a propósito: keyear remontaría el
 * árbol de rutas y con él los providers de Reservas/Caja/Comandas, que están
 * pensados para persistir entre subrutas. */
function LocationAwareBoundary({ children }) {
  const location = useLocation();
  return (
    <ChunkErrorBoundary location={location.pathname}>
      {children}
    </ChunkErrorBoundary>
  );
}

/* Puente para deep-links de notificaciones push: el service worker manda
 * {type:"OPEN_URL", url} al tocar la notif y acá navegamos con react-router
 * (en iOS PWA el navigate()/openWindow del SW es poco confiable). */
function SwNavigationBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return undefined;
    // Handler foreground de FCM: avisa de reservas de OTRA carta con la app
    // abierta (el SW solo cubre background). Idempotente, inerte sin VAPID.
    initForegroundMessages();

    // Deep-link de notificación que el SW dejó en un cache (cubre iOS PWA, donde
    // la ruta se pierde al abrir/resumir desde la notificación). Lo consumimos al
    // cargar y cada vez que la app vuelve al frente, y lo borramos para no
    // re-navegar de más. Así la reserva abre DIRECTO en su carta.
    const consumeDeepLink = async () => {
      try {
        if (typeof caches === "undefined") return;
        const c = await caches.open("rv-deeplink");
        const res = await c.match("/__deeplink");
        if (!res) return;
        await c.delete("/__deeplink");
        const url = (await res.text()) || "";
        if (!url) return;
        const u = new URL(url, window.location.origin);
        navigate(u.pathname + u.search + u.hash, { replace: true });
      } catch (e) {
        /* no-op */
      }
    };
    consumeDeepLink();
    const onVis = () => { if (!document.hidden) consumeDeepLink(); };
    document.addEventListener("visibilitychange", onVis);

    const onMsg = (e) => {
      if (e.data?.type !== "OPEN_URL" || typeof e.data.url !== "string") return;
      try {
        const u = new URL(e.data.url, window.location.origin);
        navigate(u.pathname + u.search + u.hash);
      } catch {
        window.location.assign(e.data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", onMsg);
    return () => {
      navigator.serviceWorker.removeEventListener("message", onMsg);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [navigate]);
  return null;
}

/* Oculta el AppBar global en:
 *  - rutas públicas de booking ("/book/...") — página standalone.
 *  - el módulo de reservas ("/.../reservas...") — vista operativa full-screen
 *    con su propia navegación interna (back, mes, config). */
/* Rutas que NO llevan la navbar global porque montan su propio shell con su propia barra
 * (y su propio "Volver al panel"). Poner las dos era chrome duplicado y, peor, rompía el
 * alto: `.cmd-app` mide 100dvh, así que con la barra encima el documento quedaba MÁS ALTO
 * que la ventana y la página scrolleaba sola — el Turno enfoca el input de mesa al entrar,
 * el navegador lo traía a la vista bajando, y las pestañas quedaban tapadas arriba. Había
 * clientes que no las encontraban. */
/* El admin (/admin) también: es su propia app ("RA Admin") con barra lateral en la compu y
 * pestañas abajo en el celu. */
const SIN_NAVBAR = [/^\/book\//, /\/reservas(\/|$)/, /\/caja(\/|$)/, /\/comandas(\/|$)/, /^\/admin(\/|$)/];
/* Costos sólo sin navbar con la UI nueva (v3), que dibuja su barra con el safe-area del notch.
 * La vieja (CostsScreen) cuenta con la navbar y su padding-top de 70px: para los que no están
 * en el piloto no cambia nada. */
const COSTS_PATH = /^\/profile\/[^/]+\/[^/]+\/costs(\/|$)/;

function ConditionalAppBar() {
  const location = useLocation();
  const user = useSelector(selectUser);
  // MODO LOCAL: no montamos el AppBar → no corre initAuthStateListener (onAuthStateChanged),
  // que sin sesión Firebase dispararía clearUser y borraría el user sintético → login offline.
  const hidden =
    IS_LOCAL ||
    SIN_NAVBAR.some((re) => re.test(location.pathname)) ||
    (!!user && COSTS_PATH.test(location.pathname) && isCostsV3(user.uid));

  /* El <body> reserva el hueco de la barra (index.css) la dibuje o no. Acá —el ÚNICO lugar
   * que decide si se dibuja— se sincronizan las dos cosas, así no se pueden desfasar: sin
   * barra no hay hueco. */
  useEffect(() => {
    document.body.classList.toggle("ra-no-nav", hidden);
    return () => document.body.classList.remove("ra-no-nav");
  }, [hidden]);

  if (hidden) return null;
  return <ResponsiveAppBar />;
}

export default App;
